import { useState } from "react";
import { FilePlus, FileText, Pencil, Trash2 } from "lucide-react";

import { Button } from "@/components/shadcn/button";
import { Input } from "@/components/shadcn/input";
import { cn } from "@/lib/utils";
import type { SkillConfig } from "@/generated";
import { SKILL_MD } from "./skillMd";
import { getFileContent, listFiles, validateFileName } from "./skillDraft";

type Editing = { mode: "add" } | { mode: "rename"; file: string } | null;

export const SkillFileRail = ({
  skill,
  selected,
  onSelect,
  onAdd,
  onRename,
  onDelete,
  isDisabled,
}: {
  skill: SkillConfig;
  selected: string;
  onSelect: (fileName: string) => void;
  onAdd: (fileName: string) => void;
  onRename: (from: string, to: string) => void;
  onDelete: (fileName: string) => void;
  isDisabled?: boolean;
}) => {
  const [editing, setEditing] = useState<Editing>(null);
  const [draftName, setDraftName] = useState("");

  const files = listFiles(skill);

  const reset = () => {
    setEditing(null);
    setDraftName("");
  };

  const startRename = (file: string) => {
    setEditing({ mode: "rename", file });
    setDraftName(file);
  };

  // Enter and leaving the field both commit. A name that cannot be used —
  // blank, taken, or a path — is dropped rather than held open behind an error
  // the user would have to dismiss: the file keeps the name it already had.
  const commit = () => {
    if (!editing) return;
    const name = draftName.trim();
    const isNoop = editing.mode === "rename" && name === editing.file;

    if (isNoop || validateFileName(name, skill)) {
      reset();
      return;
    }

    if (editing.mode === "add") {
      onAdd(name);
    } else {
      onRename(editing.file, name);
    }
    onSelect(name);
    reset();
  };

  const nameEditor = (
    <Input
      autoFocus
      aria-label="File name"
      // Same height as a file row, so renaming in place does not shift the list.
      className="h-9"
      value={draftName}
      placeholder="filename.md"
      disabled={isDisabled}
      onChange={(event) => setDraftName(event.target.value)}
      onBlur={commit}
      onKeyDown={(event) => {
        if (event.key === "Enter") commit();
        if (event.key === "Escape") reset();
      }}
    />
  );

  return (
    <div className="flex h-full min-h-0 flex-col gap-1">
      <p className="px-1 py-1 text-sm font-medium">Files</p>

      {/* Sized by its files, not by the space available: the list shrinks and
          starts scrolling only once it runs out of room, so the add action sits
          directly under the last file and ends up at the foot of the rail only
          when the list is long. The padding is room for the rename input's
          focus ring, which the overflow box would otherwise clip. */}
      <div className="thin-scrollbars -m-1 flex min-h-0 flex-col overflow-y-auto p-1">
        <ul className="flex flex-col gap-1">
          {files.map((fileName) => {
            const isSelected = fileName === selected;

            if (editing?.mode === "rename" && editing.file === fileName) {
              return <li key={fileName}>{nameEditor}</li>;
            }

            return (
              <li key={fileName} className="group relative">
                <Button
                  variant="ghost"
                  // Right padding reserves the overlay's space so a long
                  // name truncates instead of running under the buttons.
                  className={cn(
                    "h-9 w-full min-w-0 justify-start pr-16 font-mono hover:bg-muted/50",
                    isSelected && "bg-muted hover:bg-muted"
                  )}
                  aria-current={isSelected ? "true" : undefined}
                  onClick={() => onSelect(fileName)}
                >
                  <FileText />
                  <span className="truncate">{fileName}</span>
                  {!getFileContent(skill, fileName) && (
                    <span className="text-muted-foreground italic">empty</span>
                  )}
                </Button>
                {/* SKILL.md is the skill itself — it can be edited but not
                  renamed or removed, so it has no actions. Same hover-revealed
                  pencil/trash pair the table Columns rows use, and always shown
                  on the open file. */}
                {fileName !== SKILL_MD && (
                  <div
                    className={cn(
                      "absolute inset-y-0 right-1 hidden items-center gap-0.5",
                      "group-hover:flex focus-within:flex",
                      isSelected && "flex"
                    )}
                  >
                    <Button
                      variant="ghost"
                      size="iconsm"
                      // Ghost hover is the selected row's own background, and a
                      // translucent muted over muted is that same colour again,
                      // so on the open file the hover steps to the next shade
                      // down instead.
                      className={cn(isSelected && "hover:bg-secondary")}
                      aria-label={`Rename ${fileName}`}
                      isDisabled={isDisabled}
                      onClick={() => startRename(fileName)}
                    >
                      <Pencil className="size-3.5" />
                    </Button>
                    <Button
                      variant="ghostDestructive"
                      size="iconsm"
                      aria-label={`Delete ${fileName}`}
                      isDisabled={isDisabled}
                      onClick={() => onDelete(fileName)}
                    >
                      <Trash2 className="size-3.5" />
                    </Button>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      </div>

      <div className="pt-1">
        {editing?.mode === "add" ? (
          nameEditor
        ) : (
          <Button
            variant="outline"
            size="sm"
            isDisabled={isDisabled}
            onClick={() => {
              setEditing({ mode: "add" });
              setDraftName("");
            }}
          >
            <FilePlus />
            Add file
          </Button>
        )}
      </div>
    </div>
  );
};
